import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { BrutalButton } from '@/components/ui/BrutalButton';

interface LoginGateProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  variant?: 'overlay' | 'inline';
}

const LoginGate: React.FC<LoginGateProps> = ({ 
  children, 
  title, 
  description,
  variant = 'overlay'
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  // If authenticated, show the content
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // INLINE variant: show a brutalist box instead of children
  if (variant === 'inline') {
    return (
      <div className="border-[3px] border-primary bg-primary/5 p-5 shadow-[4px_4px_0px_0px] shadow-primary mt-4">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          {/* Lock Icon */}
          <div className="w-10 h-10 border-[2px] border-primary bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-primary" />
          </div>
          
          {/* Text */}
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-heading font-bold text-sm uppercase mb-1">
              {title || t('auth.gate.contentRestricted')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {description || t('auth.gate.registerFreeToSeeContacts')}
            </p>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 w-full sm:w-auto">
            <Link to="/auth/login" className="w-full sm:w-auto">
              <BrutalButton variant="outline" size="sm" className="w-full">
                {t('auth.gate.loginAction')}
              </BrutalButton>
            </Link>
            <Link to="/auth/register" className="w-full sm:w-auto">
              <BrutalButton size="sm" className="w-full">
                {t('auth.gate.registerFreeAction')}
              </BrutalButton>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // OVERLAY variant (default): blur content with overlay
  return (
    <div className="relative">
      {/* Blurred content - still rendered but not accessible */}
      <div className="blur-sm select-none pointer-events-none" aria-hidden="true">
        {children}
      </div>
      
      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[2px]">
        <div className="text-center p-6 max-w-sm">
          {/* Lock Icon */}
          <div className="w-14 h-14 mx-auto mb-4 border-[3px] border-dark bg-primary/10 rounded-full flex items-center justify-center shadow-[4px_4px_0px_0px] shadow-dark">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          
          {/* Title */}
          <h3 className="font-heading font-bold text-lg uppercase mb-2">
            {title || t('auth.gate.contentRestricted')}
          </h3>
          
          {/* Description */}
          <p className="text-sm text-muted-foreground mb-5">
            {description || t('auth.gate.registerFreeToSeeContacts')}
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth/login">
              <BrutalButton variant="outline" size="sm" className="w-full sm:w-auto">
                {t('auth.gate.loginAction')}
              </BrutalButton>
            </Link>
            <Link to="/auth/register">
              <BrutalButton size="sm" className="w-full sm:w-auto">
                {t('auth.gate.registerFreeAction')}
              </BrutalButton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginGate;
